import type { Severity } from "./types";

export const SAO_PAULO = "America/Sao_Paulo";
export const SUMMARY_MAX = 300;
export const GUIDANCE_MAX_LINES = 3;
export const GUIDANCE_LINE_MAX = 140;
export const NOTE_MAX = 1000;

export interface SituationFormValues {
  severity: Severity;
  summary: string;
  /** One rider-facing sentence per line. */
  guidance: string;
  scope: Record<string, string[]>;
  /** The explicit choice to reach every rider. */
  global: boolean;
  /** datetime-local values, read as São Paulo time. */
  startsAt: string;
  expiresAt: string;
  eta: string;
  internalNote: string;
  policyRef: string;
}

export type SituationFormErrors = Partial<Record<keyof SituationFormValues, string>>;
export type SituationPayloadResult =
  | { ok: true; payload: Record<string, unknown> }
  | { ok: false; errors: SituationFormErrors };

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/** ULID for a retry-safe create: the same id is sent again when the editor retries. */
export function newSituationId(now: () => number = Date.now): string {
  let time = now();
  let timePart = "";
  for (let i = 0; i < 10; i += 1) {
    timePart = CROCKFORD[time % 32] + timePart;
    time = Math.floor(time / 32);
  }
  const random = new Uint8Array(16);
  crypto.getRandomValues(random);
  const randomPart = Array.from(random, (byte) => CROCKFORD[byte % 32]).join("");
  return timePart + randomPart;
}

const LOCAL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

function saoPauloParts(instant: Date): Record<string, number> {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: SAO_PAULO,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(instant);
  return Object.fromEntries(
    parts.filter((part) => part.type !== "literal").map((part) => [part.type, Number(part.value)]),
  );
}

/** "" → undefined (not given); malformed → null; otherwise the UTC ISO instant. */
export function saoPauloLocalToIso(value: string): string | null | undefined {
  if (!value) return undefined;
  const match = LOCAL_PATTERN.exec(value);
  if (!match) return null;
  const [year, month, day, hour, minute] = match.slice(1).map(Number);
  const wanted = Date.UTC(year, month - 1, day, hour, minute);
  // Start from the wall time read as UTC, then correct by the zone offset at that instant.
  let guess = wanted;
  for (let i = 0; i < 2; i += 1) {
    const p = saoPauloParts(new Date(guess));
    const shown = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    guess += wanted - shown;
  }
  return new Date(guess).toISOString();
}

export function isoToSaoPauloLocal(iso: string): string {
  const p = saoPauloParts(new Date(iso));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

export function formatSaoPaulo(iso: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: SAO_PAULO,
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(iso));
}

/**
 * Turns the editor values into the user-experiences body. The service checks
 * everything again; this gives the editor clear messages before a request.
 */
export function buildSituationPayload(
  values: SituationFormValues,
  options: { clearEmpty?: boolean } = {},
): SituationPayloadResult {
  const errors: SituationFormErrors = {};
  const summary = values.summary.trim();
  if (!summary) errors.summary = "situations.errors.summaryRequired";
  else if (summary.length > SUMMARY_MAX) errors.summary = "situations.errors.summaryTooLong";

  const guidance = values.guidance.split("\n").map((line) => line.trim()).filter(Boolean);
  if (guidance.length > GUIDANCE_MAX_LINES) errors.guidance = "situations.errors.guidanceTooMany";
  else if (guidance.some((line) => line.length > GUIDANCE_LINE_MAX)) errors.guidance = "situations.errors.guidanceTooLong";

  const scope = Object.fromEntries(
    Object.entries(values.scope).filter(([, selected]) => selected.length > 0),
  );
  if (!values.global && Object.keys(scope).length === 0) errors.scope = "situations.errors.scopeRequired";

  const startsAt = saoPauloLocalToIso(values.startsAt);
  const expiresAt = saoPauloLocalToIso(values.expiresAt);
  const eta = saoPauloLocalToIso(values.eta);
  if (startsAt === null) errors.startsAt = "situations.errors.invalidDate";
  if (eta === null) errors.eta = "situations.errors.invalidDate";
  if (expiresAt === undefined) errors.expiresAt = "situations.errors.expiresRequired";
  else if (expiresAt === null) errors.expiresAt = "situations.errors.invalidDate";
  else if (startsAt && Date.parse(expiresAt) <= Date.parse(startsAt)) {
    errors.expiresAt = "situations.errors.expiresBeforeStart";
  }

  const internalNote = values.internalNote.trim();
  if (internalNote.length > NOTE_MAX) errors.internalNote = "situations.errors.noteTooLong";
  const policyRef = values.policyRef.trim();

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const optional = (key: string, value: string | undefined | null) =>
    value ? { [key]: value } : options.clearEmpty ? { [key]: null } : {};
  return {
    ok: true,
    payload: {
      severity: values.severity,
      summary,
      guidance,
      scope: values.global ? {} : scope,
      ...(values.global ? { scope_global: true } : {}),
      ...(startsAt ? { starts_at: startsAt } : {}),
      expires_at: expiresAt,
      ...optional("eta", eta),
      ...optional("internal_note", internalNote),
      ...optional("policy_ref", policyRef),
    },
  };
}
